import { forwardRef, type ReactNode } from 'react';
import { Icon } from './Icon';
import { Kbd } from './Kbd';
import './SearchField.css';

export interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label: string;
  /** A key-cap hint shown at the field's trailing edge while empty and unfocused (e.g. `/`) —
   * DESIGN.md §9.1 "SearchField (with clear + shortcut hint)". Hidden once there is a value, since
   * the clear button takes that space. */
  shortcutHint?: string;
  /** The clear button's accessible name. A prop rather than a `useT()` call inside this primitive —
   * `ui/` components stay dictionary-free so they can render under `node --test` and outside
   * `I18nProvider`. This is the rule the other primitives' label props all point back to.
   *
   * MVP-357: required. MVP-272 Phase 1 gave it an English `?? 'Clear search'` default to spare the
   * surfaces not yet translated; both of them are translated now, so the default was the last
   * English string on a Thai screen rather than a transitional convenience. */
  clearLabel: string;
  className?: string;
  id?: string;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  autoComplete?: string;
  /** Rendered instead of the shortcut hint, e.g. a small inline spinner while a request is in flight. */
  trailing?: ReactNode;
  /** MVP-608. The `id` of a `role="listbox"` this field drives with the arrow keys. Passing it
   * makes the input a `role="combobox"` — the pattern where the cursor moves through the list
   * while DOM focus stays in the text box, which is exactly what MVP-593 left this screen doing
   * silently. Omit it (the default) and nothing about the field changes.
   *
   * Left undefined by the caller while the list is not on screen, since `aria-controls` pointing
   * at an element that does not exist is a dangling reference. The ROLE stays put either way: a
   * control whose role flips as results arrive is worse than a collapsed combobox. */
  listboxId?: string;
  /** Whether that listbox is currently showing options — the combobox's `aria-expanded`. Setting
   * this at all (even to `false`) is what opts the field into `role="combobox"`, so the role is
   * stable across the empty-result state where `listboxId` goes away. */
  listboxExpanded?: boolean;
  /** MVP-608. The `id` of the option the roving cursor is on, or `undefined` to announce nothing.
   * The caller is responsible for this naming an element that is really in the DOM; see
   * `features/library/activeDescendant.ts` for why a virtualised list makes that a real decision. */
  activeDescendantId?: string;
}

/** DESIGN.md §9.1 `SearchField`. The library toolbar's primary control (AC-UI-3) — a leading
 * search glyph, a clear button that appears once there is a query, and a discoverable `/` hint. */
export const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(function SearchField(
  {
    value,
    onChange,
    placeholder,
    label,
    clearLabel,
    shortcutHint,
    className,
    id,
    onKeyDown,
    autoComplete,
    trailing,
    listboxId,
    listboxExpanded,
    activeDescendantId,
  },
  ref
) {
  return (
    <div className={['search-field', className].filter(Boolean).join(' ')}>
      <span className="search-field__icon">
        <Icon name="search" size={18} />
      </span>
      <input
        ref={ref}
        id={id}
        type="text"
        className="search-field__input text-body"
        value={value}
        placeholder={placeholder}
        aria-label={label}
        // MVP-608. All five collapse to `undefined` for every caller that does not opt in, so the
        // field keeps rendering as the plain text box it has always been.
        role={listboxExpanded === undefined ? undefined : 'combobox'}
        aria-expanded={listboxExpanded}
        aria-controls={listboxId}
        aria-autocomplete={listboxExpanded === undefined ? undefined : 'list'}
        aria-activedescendant={activeDescendantId}
        autoComplete={autoComplete ?? 'off'}
        spellCheck={false}
        onChange={(e) => onChange(e.currentTarget.value)}
        onKeyDown={onKeyDown}
      />
      <span className="search-field__end">
        {trailing}
        {!trailing && value && (
          <button
            type="button"
            className="search-field__clear"
            aria-label={clearLabel}
            onClick={() => onChange('')}
          >
            <Icon name="close" size={14} />
          </button>
        )}
        {!trailing && !value && shortcutHint && <Kbd>{shortcutHint}</Kbd>}
      </span>
    </div>
  );
});
